import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../../context/AppContext';
import UserAPI from '../../../api/user';
import AxiosService from '../../../lib/axios';
import useErrorHandler from '../../../shared/hooks/useErrorHandler';

/**
 * Hook personalizado para manejar el perfil del usuario actual
 * Reemplaza ProfileController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar el perfil
 */
export const useProfile = () => {
    const { notify } = useApp();
    const handleError = useErrorHandler();
    
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [oldPassword, setOldPassword] = useState('');
    const [modalConfirm, setModalConfirm] = useState(false);
    const [action, setAction] = useState(null);
    const [id, setId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Cargar perfil del usuario actual
     */
    const loadProfile = useCallback(async () => {
        setIsLoading(true);
        try {
            const user = await AxiosService.getPayload();
            if (!user || !user.id) {
                throw new Error('No se pudo obtener la información del usuario');
            }

            const profile = await UserAPI.getById(user.id);
            if (profile) {
                setName(profile.name || '');
                setEmail(profile.email || '');
                setId(profile._id);
            }
        } catch (error) {
            handleError(error, 'Error al cargar el perfil');
        } finally {
            setIsLoading(false);
        }
    }, [handleError]);

    /**
     * Actualizar perfil
     */
    const update = useCallback(async () => {
        if (!id) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "No se ha cargado el perfil" 
            });
            return;
        }

        setIsLoading(true);
        try {
            const data = { email, name };
            await UserAPI.update(id, data);
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Perfil actualizado correctamente" 
            });
            setModalConfirm(false);
        } catch (error) {
            handleError(error, 'Error al actualizar el perfil');
        } finally {
            setIsLoading(false);
        }
    }, [id, email, name, notify, handleError]);

    /**
     * Actualizar contraseña
     */
    const updatePassword = useCallback(async () => {
        if (!id) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "No se ha cargado el perfil" 
            });
            return;
        }

        if (password !== newPassword) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "Las contraseñas no coinciden" 
            });
            return;
        }

        if (!password || !newPassword || !oldPassword) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "Por favor complete todos los campos de contraseña" 
            });
            return;
        }

        setIsLoading(true);
        try {
            await UserAPI.update(id, { currentPassword: oldPassword, newPassword: password });
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Contraseña actualizada correctamente" 
            });
            setModalConfirm(false);
            // Limpiar campos de contraseña
            setPassword('');
            setNewPassword('');
            setOldPassword('');
        } catch (error) {
            handleError(error, 'Error al actualizar la contraseña');
        } finally {
            setIsLoading(false);
        }
    }, [id, password, newPassword, oldPassword, notify, handleError]);

    /**
     * Preparar actualización de perfil
     */
    const callUpdate = useCallback(() => {
        setAction("update");
        setModalConfirm(true);
    }, []);

    /**
     * Preparar actualización de contraseña
     */
    const callUpdatePassword = useCallback(() => {
        setAction("updatePassword");
        setModalConfirm(true);
    }, []);

    /**
     * Ejecutar acción según el tipo
     */
    const callAction = useCallback(() => {
        switch (action) {
            case "update":
                return update();
            case "updatePassword":
                return updatePassword();
            default:
                notify({ 
                    title: "Error", 
                    type: "error", 
                    text: "Acción no válida" 
                });
        }
    }, [action, update, updatePassword, notify]);

    // Cargar perfil al montar
    useEffect(() => {
        loadProfile();
    }, [loadProfile]);

    return {
        // Datos del perfil
        name,
        setName,
        email,
        setEmail,
        id,
        
        // Contraseña
        password,
        setPassword,
        newPassword,
        setNewPassword,
        oldPassword,
        setOldPassword,
        
        // Modales y acciones
        modalConfirm,
        setModalConfirm,
        action,
        setAction,
        
        // Estado de carga
        isLoading,
        
        // Funciones
        loadProfile,
        update,
        updatePassword,
        callUpdate,
        callUpdatePassword,
        callAction,
    };
};

export default useProfile;

